import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import BlurReveal from '../components/Reveal/BlurReveal';
import { BlurScrollEffect } from '../effects/blurScrollEffect';
import { PROJECTS } from '../data/projects';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import matteImage from '../assets/portfolio/Matteo_1.webp';
import logoABC from '../assets/images/logo_ABC.webp';
import logoFutureTherapy from '../assets/images/logo_FutureTherapy.webp';
import logoGoodGrapes from '../assets/images/logo_GoodGrapes.webp';
import logoLlama from '../assets/images/logo_Llama.webp';
import logoMatteo from '../assets/images/logo_Matteo.webp';
import logoSonaris from '../assets/images/logo_Sonaris.webp';
import logoSuperGuage from '../assets/images/logo_SuperGuage.webp';
import newIcon1 from '../assets/images/newicon1.svg';
import newIcon2 from '../assets/images/newicon2.svg';
import newIcon3 from '../assets/images/newicon3.svg';
import newIcon4 from '../assets/images/newicon4.svg';
import newIcon5 from '../assets/images/newicon5.svg';
import newIcon6 from '../assets/images/newicon6.svg';
import newIcon7 from '../assets/images/newicon7.svg';
import newIcon8 from '../assets/images/newicon8.svg';
import newIcon1Red from '../assets/images/newicon1red.svg';
import newIcon2Red from '../assets/images/newicon2red.svg';
import newIcon3Red from '../assets/images/newicon3red.svg';

gsap.registerPlugin(ScrollTrigger);

const PARTNER_LOGOS = [
  { src: logoABC, alt: 'ABC' },
  { src: logoFutureTherapy, alt: 'Future Therapy' },
  { src: logoGoodGrapes, alt: 'Good Grapes' },
  { src: logoLlama, alt: 'Llama' },
  { src: logoMatteo, alt: 'Matteo' },
  { src: logoSonaris, alt: 'Sonaris' },
  { src: logoSuperGuage, alt: 'Super Guage' },
];

const IntroTypewriterOnScroll = ({ text }) => {
  const [displayed, setDisplayed] = useState('');
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let observer;
    let intervalId;

    const startTyping = () => {
      if (started) return;
      setStarted(true);

      let index = 0;
      intervalId = window.setInterval(() => {
        index += 1;
        setDisplayed(text.slice(0, index));
        if (index >= text.length) {
          window.clearInterval(intervalId);
        }
      }, 18);
    };

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startTyping();
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(el);

    return () => {
      if (observer) observer.disconnect();
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [started, text]);

  return (
    <span
      ref={ref}
      style={{ display: 'inline-block', minHeight: '1em' }}
    >
      {displayed}
    </span>
  );
};

const Home = () => {
  const blurTextRef = useRef(null);
  const heroRef = useRef(null);
  const heroMediaRef = useRef(null);
  const formatGmtTime = () => {
    const timeString = new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Europe/London'
    });

    return `${timeString.toUpperCase().replace(' ', ' ')} GMT`;
  };
  const [localTime, setLocalTime] = useState(formatGmtTime);

  useEffect(() => {
    const tick = () => {
      setLocalTime(formatGmtTime());
    };

    const intervalId = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const heroEl = heroRef.current;
    const mediaEl = heroMediaRef.current;
    if (!heroEl || !mediaEl) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        mediaEl,
        { y: -50, scale: 1.12 },
        {
          y: 50,
          scale: 1.12,
          ease: 'none',
          scrollTrigger: {
            trigger: heroEl,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    });

    return () => ctx.revert();
  }, []);

  

  useEffect(() => {
    if (!blurTextRef.current) return;

    new BlurScrollEffect(blurTextRef.current);
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        return;
      }
    }

    const rows = document.querySelectorAll('.section.portfolio .row');
    if (!rows.length) return;

    const animations = [];

    rows.forEach((row) => {
      const items = row.querySelectorAll('.portfolio-grid-item');
      if (!items.length) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: row,
          start: 'top 90%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.fromTo(
        items,
        { y: 40, filter: 'blur(8px)' },
        {
          y: 0,
          filter: 'blur(0px)',
          duration: 0.9,
          ease: 'power2.out',
          stagger: 0.12,
        }
      );

      animations.push(tl);
    });

    return () => {
      animations.forEach((tl) => {
        if (tl.scrollTrigger) {
          tl.scrollTrigger.kill();
        }
        tl.kill();
      });
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        return;
      }
    }

    const cardsRow = document.querySelector('.home .cards-row');
    if (!cardsRow) return;

    const cards = cardsRow.querySelectorAll('.strategy-card');
    if (!cards.length) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: cardsRow,
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    });

    tl.fromTo(
      cards,
      { opacity: 0, y: 35, filter: 'blur(4px)' },
      {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        duration: 0.7,
        ease: 'power2.out',
        stagger: 0.18,
      }
    );

    return () => {
      if (tl.scrollTrigger) {
        tl.scrollTrigger.kill();
      }
      tl.kill();
    };
  }, []);

  useEffect(() => {
    const ctaInner = document.querySelector('.section.cta .cta-inner');
    if (!ctaInner) return;

    // Disable CTA scroll animation on small screens so content is always visible
    if (typeof window !== 'undefined' && window.matchMedia) {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        return;
      }
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) return;

    const targets = ctaInner.querySelectorAll('.cta-title, .cta-copy, .cta-button');
    if (!targets.length) return;

    const anim = gsap.fromTo(
      targets,
      { opacity: 0, y: 24 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power2.out',
        stagger: 0.12,
        scrollTrigger: {
          trigger: ctaInner,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      }
    );

    return () => {
      if (anim.scrollTrigger) {
        anim.scrollTrigger.kill();
      }
      anim.kill();
    };
  }, []);


  const carouselItems = useMemo(
    () =>
      PROJECTS.map((project) => {
        const heroBlock = project.blocks.find((block) => block.type === 'image');
        return {
          slug: project.slug,
          title: project.title,
          subtitle: project.subtitle,
          image: heroBlock ? heroBlock.src : null,
        };
      }).filter((project) => project.image),
    []
  );
  return (
    <>
      <Seo />
      <div className="home">
      <section className="home-hero" ref={heroRef}>
        <div className="home-hero__media" ref={heroMediaRef} />
        <div className="home-hero__meta">
          <span className="home-hero__meta-location">Glasgow, Scotland</span>
          <span className="home-hero__meta-time mono" aria-live="polite">
            {localTime}
          </span>
        </div>
      </section>

      <section className="section intro">
        <div className="wrapper grid-display">
          <div className="row">

          

            <div className="col-12">
              <section className="content">
                {/* Desktop: animated blur text */}
                <div
                  className="blur-text blur-text--1 large introtext introtext--desktop center-text"
                  data-effect-1
                  ref={blurTextRef}
                >
                  <h1 className="">Product design that prioritises making positive impact for people, businesses and communities.
</h1>
                  <p className="introtext__subtitle">
                    Working with forward-thinking weirdos and outsiders to create products and services that actually make a difference.
                  </p>
                </div>

                {/* Mobile: static, non-blurred text */}
                <div className="large introtext introtext--mobile">
Product design that prioritises making positive impact for people, businesses and communities.</div>
              </section>
            </div>

          </div>

          <div className="row cards-row">
            <div className="">
              <div className="strategy-card">
                <div className="strategy-card-image">
                  <img src={newIcon1} alt="UX & UI Design icon" />
                </div>
                <div className="strategy-card-content">
                  <h3 className="strategy-card-title">UX & UI Design</h3>
                  <p className="strategy-card-text">
                    Research-led. User-obsessed. Actually useful. From discovery and research through to wireframes and prototypes, design with intention, not assumption.
                  </p>
                </div>
              </div>
            </div>

            <div className="">
              <div className="strategy-card">
                <div className="strategy-card-image">
                  <img src={newIcon2} alt="Product Design icon" />
                </div>
                <div className="strategy-card-content">
                  <h3 className="strategy-card-title">Product Design</h3>
                  <p className="strategy-card-text">
                    I turn ideas into functional products through research, iteration, and collaboration. From early discovery to polished delivery, every step is guided by real user insight—not assumptions.
                  </p>
                </div>
              </div>
            </div>

            <div className="">
              <div className="strategy-card">
                <div className="strategy-card-image">
                  <img src={newIcon3} alt="Web Design icon" />
                </div>
                <div className="strategy-card-content">
                  <h3 className="strategy-card-title">Web Design</h3>
                  <p className="strategy-card-text">
                    From strategy to execution, we design websites that balance business goals with user needs. The result is fast, accessible, and engaging digital experiences that perform in the real world.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
         
        </div>
      </section>

      <section className="section portfolio">
        <div className="wrapper">
          <div className="portfolio-heading-row">
            <h2 className="portfolio-heading">Latest Work</h2>
          </div>
        </div>
        <div className="wrapper grid-display">
          {/* First row: col-8 and col-4 */}
          <div className="row">
            {carouselItems.slice(0, 2).map((item, index) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link
                  to={`/projects/${item.slug}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          
          {/* Second row: 3xcol-4 items */}
          <div className="row">
            {carouselItems.slice(2, 5).map((item) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link
                  to={`/projects/${item.slug}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* Third row: full width col-12 */}
          <div className="row full-height">
            {carouselItems.slice(5, 6).map((item) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link
                  to={`/projects/${item.slug}`}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Home;
